import { useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import logo from "@/assets/habits365greek-logo.png";
import { PortalSidebar } from "@/components/navigation/PortalSidebar";
import { PortalFooter } from "@/components/PortalFooter";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfileDisplayNames } from "@/hooks/useProfileDisplayNames";
import { DESIGNER_NAV } from "../nav";

/**
 * DesignerShell — the designer portal's layout chrome. Mirrors `ClientShell` so
 * all three portals look + behave identically, feeding the SHARED `PortalSidebar`
 * the designer nav registry + brand. Identity comes from the STAFF auth context
 * (`useAuth`), not the client one.
 *
 * Sign-out is the ONLY place this portal ends the session, and only on an
 * explicit user click — the internal + client portals share one Supabase
 * session, so guards redirect (never sign out) to avoid killing the other
 * portal's session.
 */

interface DesignerShellProps {
  children: React.ReactNode;
}

const DesignerBrand = () => (
  <Link to="/designer/work" className="flex items-center gap-2">
    <img
      src={logo}
      alt="Habits 365 Greek"
      className="h-7 object-contain dark:invert dark:brightness-200"
    />
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
      Designer
    </span>
  </Link>
);

const DesignerSidebarFooter = () => (
  <div className="text-xs text-muted-foreground">
    Design studio
    <br />
    <a href="mailto:hello@habits365greek.com" className="text-primary hover:underline">
      hello@habits365greek.com
    </a>
  </div>
);

export const DesignerShell = ({ children }: DesignerShellProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const ids = useMemo(() => (user?.id ? [user.id] : []), [user?.id]);
  const { getDisplayName } = useProfileDisplayNames(ids);
  const fullName =
    (user?.id ? getDisplayName(user.id) : "") || user?.email || "Designer";

  const handleSignOut = async () => {
    /** Explicit, user-initiated only — see file header (shared session). */
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate("/signin");
  };

  const initials =
    fullName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Shared desktop sidebar */}
      <div className="sticky top-0 hidden h-screen md:block">
        <PortalSidebar
          className="h-screen"
          ariaLabel="Designer navigation"
          groups={[{ items: DESIGNER_NAV }]}
          header={<DesignerBrand />}
          footer={<DesignerSidebarFooter />}
        />
      </div>

      {/* Main column */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-sticky border-b border-border bg-navbar/95 text-navbar-foreground backdrop-blur-sm">
          <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
              aria-controls="designer-mobile-nav"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            <Link to="/designer/unclaimed" className="flex items-center gap-2 md:hidden">
              <img
                src={logo}
                alt="Habits 365 Greek"
                className="h-8 object-contain dark:invert dark:brightness-200"
              />
            </Link>

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
                    aria-label="Account menu"
                  >
                    {initials}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <div className="truncate text-sm font-semibold">{fullName}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">
                      {user?.email}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile nav drawer — reads the SAME DESIGNER_NAV (no second list) */}
          {mobileOpen && (
            <nav
              id="designer-mobile-nav"
              className="border-t border-border bg-background/95 p-3 backdrop-blur md:hidden"
            >
              {DESIGNER_NAV.map((item) => {
                const Icon = item.icon;
                const active =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
                      active
                        ? "bg-sidebar-active text-sidebar-active-foreground"
                        : "text-muted-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 flex-shrink-0", item.iconClass)} />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          )}
        </header>

        {/* Page content — full-width, padding matched to the other portals */}
        <main className="flex flex-1 flex-col overflow-auto px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>

        <PortalFooter />
      </div>
    </div>
  );
};

export default DesignerShell;
